import { usePickers } from '@/hooks/usePickers';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, MessageCircle, ShieldCheck } from 'lucide-react';
import { ChildRecords } from '@/components/ChildRecords';
import { Drawer, Field, StatusPill, formatCurrency, formatDate, formatDateTime, formatNumber, todayIso } from '@/components/live';
import { ValidityBadge } from '@/components/ValidityBadge';
import { callRpc } from '@/api/resources';
import { plotOptions } from '@/api/lookups';
import { OTP_FAILURES, otpChannels, sendOtp, verifyOtp, type IssuedOtp, type OtpChannel } from '@/api/otp';
import { useRows } from '@/hooks/useResource';
import { isSupabaseConfigured } from '@/lib/supabase';
import { toast } from '@/lib/toast';
import { PAYMENT_MODES, QUANTITY_OF, RATE_BASES, SAMPLES, WORK_ACTIVITIES, optionLabel } from '@/lib/practiceLists';
import type { WorkerRow } from '@/screens/parts/WorkerDrawer';

interface AgreementRow {
  id: string; agreement_code: string | null; activity: string; activity_detail: string | null; rate_basis: string; rate: number;
  unit_label: string | null; valid_from: string; valid_to: string; agreement_status: string; acknowledged_at: string | null;
  acknowledgement_method: string | null; plot_code: string | null; summary_message: string;
}
interface PaymentRow {
  id: string; payment_code: string | null; agreement_code: string | null; activity: string; work_from: string; work_to: string;
  quantity: number; quantity_basis: string; rate: number; amount_due: number; deductions: number; amount_paid: number;
  balance_due: number; paid_on: string; payment_mode: string; receipt_confirmed_at: string | null; receipt_method: string | null;
  summary_message: string; plot_code: string | null;
}

/** A WhatsApp link that opens the chat with the summary typed in; the field officer presses send. */
function whatsappLink(worker: WorkerRow, text: string): string | null {
  const number = (worker.whatsapp_number || worker.phone || '').replace(/\D/g, '');
  return number ? `https://wa.me/${number}?text=${encodeURIComponent(text)}` : null;
}

/**
 * A worker's agreed rates and the payments made to them (owner, 2026-09-21: "agreed rate per
 * activity, and payment made, otp verification for receipt of payment and payment record ... This
 * agreed payment consent forms a short message for whatsapp as summary to worker").
 *
 * An agreement is acknowledged by the worker reading back a code sent to their own phone (or with a
 * signed paper copy); once acknowledged its rate is fixed. A payment's receipt is confirmed the
 * same way; once confirmed it cannot be changed. Each carries a one-message summary, shared on
 * WhatsApp.
 */
export function WorkerAgreementsAndPayments({ worker }: { worker: WorkerRow }) {
  const [confirming, setConfirming] = useState<{ entity: 'agreement' | 'payment'; id: string; label: string } | null>(null);
  const agreements = useRows<AgreementRow>('worker_rate_agreements', { filters: { worker_id: worker.id } });
  const plots = useQuery({ queryKey: ['picker', 'plots', ''], queryFn: () => plotOptions(), enabled: isSupabaseConfigured });
  const pick = usePickers(['sites']);
  const agreementOptions = (agreements.data ?? []).filter((row) => row.agreement_status !== 'cancelled').map((row) => ({
    id: row.id,
    label: `${row.agreement_code ?? ''} ${optionLabel(WORK_ACTIVITIES, row.activity)} - Rs ${row.rate} ${optionLabel(RATE_BASES, row.rate_basis).toLowerCase()}${row.agreement_status === 'draft' ? ' (not yet acknowledged)' : ''}`,
  }));

  const share = (text: string) => {
    const link = whatsappLink(worker, text);
    return link
      ? <a className="button-link" href={link} rel="noreferrer" target="_blank" title="Open WhatsApp with this summary"><MessageCircle size={15} /> WhatsApp</a>
      : null;
  };

  return (
    <div className="worker-money">
      <ChildRecords<AgreementRow>
        addLabel="Add agreed rate" emptyText="No rate agreed yet. Agree a rate per activity; the worker acknowledges it with a code."
        extra={(row) => (
          <>
            {share(row.summary_message)}
            {row.agreement_status === 'draft' && (
              <button onClick={() => setConfirming({ entity: 'agreement', id: row.id, label: `agreement ${row.agreement_code ?? ''}` })} type="button">
                <ShieldCheck size={15} /> Worker acknowledges
              </button>
            )}
          </>
        )}
        fields={[
          { key: 'activity', label: 'Activity', type: 'select', options: WORK_ACTIVITIES, required: true },
          { key: 'activity_detail', label: 'Detail', type: 'text', suggestions: SAMPLES.activityDetail },
          { key: 'rate_basis', label: 'Rate basis', type: 'select', options: RATE_BASES, required: true },
          { key: 'rate', label: 'Agreed rate (₹)', type: 'number', required: true },
          { key: 'unit_label', label: 'Unit', type: 'text', required: true, suggestions: SAMPLES.unitLabel, showIf: (form) => form.rate_basis === 'per_unit' },
          { key: 'plot_id', label: 'Plot (optional)', type: 'select', options: plots.data ?? [], placeholder: 'Any plot' },
          { key: 'site_id', label: 'Site (optional)', type: 'select', options: pick.sites, placeholder: 'Any site', showIf: (form) => !form.plot_id },
          { key: 'valid_from', label: 'Valid from', type: 'date', required: true },
          { key: 'valid_to', label: 'Valid to', type: 'date', required: true },
          { key: 'acknowledgement_method', label: 'Worker acknowledges by', type: 'select',
            options: [{ id: 'signature', label: 'Signed / thumb-printed paper copy' }], placeholder: 'Code sent to the worker (OTP)' },
          { key: 'signed_document_id', label: 'Signed copy', type: 'file', required: true, showIf: (form) => form.acknowledgement_method === 'signature' },
          { key: 'remarks', label: 'Remarks', type: 'textarea' },
        ]}
        defaults={{ valid_from: todayIso(), rate_basis: 'per_day', site_id: worker.site_id ?? '' }}
        filters={{ worker_id: worker.id }} orderBy={{ column: 'valid_from', ascending: false }} ownerId={worker.id}
        parent={{ worker_id: worker.id }} screenKey="party.register"
        summary={(row) => ({
          title: `${optionLabel(WORK_ACTIVITIES, row.activity)}: Rs ${formatNumber(row.rate, 2)} ${row.rate_basis === 'per_unit' ? `per ${row.unit_label ?? 'unit'}` : optionLabel(RATE_BASES, row.rate_basis).toLowerCase()}`,
          badge: <>{row.agreement_status === 'acknowledged'
            ? <span className="badge badge-green"><CheckCircle2 size={12} /> Acknowledged</span> : <StatusPill value={row.agreement_status} />}
            {' '}<ValidityBadge compact from={row.valid_from} to={row.valid_to} /></>,
          detail: [`${formatDate(row.valid_from)} to ${formatDate(row.valid_to)}`, row.plot_code,
            row.acknowledged_at ? `acknowledged ${formatDateTime(row.acknowledged_at)} by ${row.acknowledgement_method === 'signature' ? 'signed copy' : 'code'}` : null,
            row.agreement_code].filter(Boolean).join(' · '),
        })}
        title="Agreed rates"
        validate={(form) => ({
          rate: form.rate && !(Number(form.rate) > 0) ? 'The rate must be more than 0.' : undefined,
          valid_to: form.valid_to && String(form.valid_to) <= String(form.valid_from) ? 'It must end after it starts.' : undefined,
        })}
        table="ops.worker_rate_agreements" view="worker_rate_agreements" />

      <ChildRecords<PaymentRow>
        addLabel="Record payment" emptyText="No payments recorded."
        extra={(row) => (
          <>
            {share(row.summary_message)}
            {!row.receipt_confirmed_at && (
              <button onClick={() => setConfirming({ entity: 'payment', id: row.id, label: `payment ${row.payment_code ?? ''}` })} type="button">
                <ShieldCheck size={15} /> Worker confirms receipt
              </button>
            )}
          </>
        )}
        fields={[
          { key: 'agreement_id', label: 'Agreed rate', type: 'select', options: agreementOptions, placeholder: 'No agreement - enter the rate' },
          { key: 'activity', label: 'Activity', type: 'select', options: WORK_ACTIVITIES, required: true, showIf: (form) => !form.agreement_id },
          { key: 'quantity_basis', label: 'Rate basis', type: 'select', options: RATE_BASES, required: true, showIf: (form) => !form.agreement_id },
          { key: 'rate', label: 'Rate (₹)', type: 'number', required: true, showIf: (form) => !form.agreement_id },
          { key: 'plot_id', label: 'Plot', type: 'select', options: plots.data ?? [], placeholder: 'From the agreement / none' },
          { key: 'site_id', label: 'Site', type: 'select', options: pick.sites, placeholder: 'From the agreement / none', showIf: (form) => !form.plot_id },
          { key: 'work_from', label: 'Work from', type: 'date', required: true },
          { key: 'work_to', label: 'Work to', type: 'date', required: true },
          { key: 'quantity', label: 'Quantity (days, hours, trees, kg or units)', type: 'number', required: true,
            hint: 'In the agreed basis: days for a daily rate, hours for an hourly rate ...' },
          { key: 'hours_worked', label: 'Total hours worked', type: 'number' },
          { key: 'deductions', label: 'Deductions (₹)', type: 'number' },
          { key: 'deduction_reason', label: 'Deduction reason', type: 'text', required: true, suggestions: SAMPLES.deduction, showIf: (form) => Number(form.deductions) > 0 },
          { key: 'amount_paid', label: 'Amount paid (₹)', type: 'number', required: true, hint: 'The amount due is worked out from the rate and quantity.' },
          { key: 'paid_on', label: 'Paid on', type: 'date', required: true, max: todayIso() },
          { key: 'payment_mode', label: 'Payment mode', type: 'select', options: PAYMENT_MODES, required: true },
          { key: 'payment_reference', label: 'UPI / bank / cheque reference', type: 'text', required: true, placeholder: 'e.g. UPI 426512345678', showIf: (form) => form.payment_mode !== 'cash' && Boolean(form.payment_mode) },
          { key: 'receipt_method', label: 'Worker confirms receipt by', type: 'select',
            options: [{ id: 'signature', label: 'Signed / thumb-printed voucher' }], placeholder: 'Code sent to the worker (OTP)' },
          { key: 'receipt_document_id', label: 'Signed voucher', type: 'file', required: true, showIf: (form) => form.receipt_method === 'signature' },
          { key: 'remarks', label: 'Remarks', type: 'textarea' },
        ]}
        defaults={{ paid_on: todayIso(), payment_mode: 'cash', deductions: '0', site_id: worker.site_id ?? '' }}
        filters={{ worker_id: worker.id }} orderBy={{ column: 'paid_on', ascending: false }} ownerId={worker.id}
        parent={{ worker_id: worker.id }} screenKey="party.register"
        summary={(row) => ({
          title: `${formatCurrency(row.amount_paid)} paid ${formatDate(row.paid_on)} - ${optionLabel(WORK_ACTIVITIES, row.activity)}`,
          badge: row.receipt_confirmed_at
            ? <span className="badge badge-green"><CheckCircle2 size={12} /> Receipt confirmed</span>
            : <span className="badge badge-amber">Receipt not confirmed</span>,
          detail: [`${formatDate(row.work_from)} to ${formatDate(row.work_to)}`,
            `${formatNumber(row.quantity, 2)} ${(QUANTITY_OF[row.quantity_basis] ?? '').toLowerCase()} at Rs ${formatNumber(row.rate, 2)}`,
            `due ${formatCurrency(row.amount_due)}`, row.deductions > 0 ? `less ${formatCurrency(row.deductions)}` : null,
            row.balance_due > 0 ? `balance ${formatCurrency(row.balance_due)}` : null, optionLabel(PAYMENT_MODES, row.payment_mode),
            row.receipt_confirmed_at ? `confirmed ${formatDateTime(row.receipt_confirmed_at)}` : null, row.payment_code].filter(Boolean).join(' · '),
        })}
        title="Payments"
        validate={(form) => ({
          work_to: form.work_to && String(form.work_to) < String(form.work_from) ? 'Cannot end before it starts.' : undefined,
          paid_on: form.paid_on && String(form.paid_on) < String(form.work_from) ? 'Payment cannot be before the work started.' : undefined,
          quantity: form.quantity && !(Number(form.quantity) > 0) ? 'More than 0.' : undefined,
        })}
        table="ops.worker_payments" view="worker_payments" />

      {confirming && <WorkerCodeConfirm onClose={() => setConfirming(null)} target={confirming} worker={worker} />}
    </div>
  );
}

/**
 * The worker reads back a code sent to their own number: the agreement is acknowledged, or the
 * payment's receipt confirmed. The code goes only to the contacts on the worker's record.
 */
function WorkerCodeConfirm({ worker, target, onClose }: {
  worker: WorkerRow; target: { entity: 'agreement' | 'payment'; id: string; label: string }; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [issued, setIssued] = useState<IssuedOtp | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const channels = useQuery({
    queryKey: ['otp-channels', worker.party_id], queryFn: () => otpChannels(worker.party_id ?? undefined),
    enabled: isSupabaseConfigured && Boolean(worker.party_id),
  });
  const available = (channels.data ?? []).filter((entry) => entry.available && entry.channel !== 'email');

  async function send(channel: OtpChannel['channel']) {
    if (!worker.party_id) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await sendOtp({
        purpose: 'acknowledgement', channel, partyId: worker.party_id,
        linkedTable: target.entity === 'agreement' ? 'ops.worker_rate_agreements' : 'ops.worker_payments', linkedId: target.id,
      });
      setIssued(result);
      toast.success(`Code sent to ${result.destination_masked}.`);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'The code could not be sent.');
    } finally {
      setBusy(false);
    }
  }

  async function confirm() {
    if (!issued) return;
    setBusy(true);
    setError(undefined);
    try {
      const result = await verifyOtp(issued.otp_event_id, code.trim());
      if (!result.verified) {
        setError(OTP_FAILURES[result.failure_reason ?? ''] ?? 'That code could not be verified.');
        return;
      }
      await callRpc('fn_confirm_worker_otp', { p_entity: target.entity, p_id: target.id, p_otp_event_id: issued.otp_event_id });
      await queryClient.invalidateQueries({ queryKey: [target.entity === 'agreement' ? 'worker_rate_agreements' : 'worker_payments'] });
      await queryClient.invalidateQueries({ queryKey: ['workers'] });
      toast.success(target.entity === 'agreement' ? 'The worker acknowledged the agreed rate.' : 'The worker confirmed receiving the payment.');
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'That could not be recorded.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer
      footer={
        <>
          <button className="cancel" onClick={onClose} type="button">Cancel</button>
          {issued && <button className="primary" disabled={busy || code.trim().length < 4} onClick={() => void confirm()} type="button">Verify and confirm</button>}
        </>
      }
      onClose={onClose}
      open
      subtitle={`${worker.full_name} reads back the code sent to their own phone.`}
      title={target.entity === 'agreement' ? 'Worker acknowledges the rate' : 'Worker confirms receipt'}
    >
      {!worker.party_id || (channels.isSuccess && available.length === 0) ? (
        <p className="field-error">No phone or WhatsApp number is recorded for {worker.full_name}. Add one on the worker record, or use a signed paper copy.</p>
      ) : (
        <div className="otp-channel-list">
          {available.map((entry) => (
            <button disabled={busy} key={entry.channel} onClick={() => void send(entry.channel)} type="button">
              Send by {entry.channel === 'sms' ? 'SMS' : 'WhatsApp'} to {entry.destination_masked}
            </button>
          ))}
        </div>
      )}
      {issued && (
        <Field error={error} label={`Code sent to ${issued.destination_masked}`} required>
          <input autoFocus inputMode="numeric" maxLength={6} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} value={code} />
        </Field>
      )}
      {!issued && error && <p className="field-error">{error}</p>}
      <p className="muted">For {target.label}.</p>
    </Drawer>
  );
}
